/**
 * `can()`: the pure core of the SPA's `useCan(permission, feature?)` hook.
 *
 * It calls the same `authorize()` the API runs, and folds the decision into what a nav item
 * or button needs: `allowed`; `locked` (role permits, plan does not: show a lock and open the
 * upsell dialog with `feature`/`reason`); `hidden` (role or membership forbids: render
 * nothing, because the route would be denied too).
 */
import type { Actor } from './actor.js';
import { authorize, type Decision } from './authorize.js';
import type { EntitlementDenyReason } from './entitlements.js';
import type { Permission } from './permissions/index.js';
import type { PlatformPermission } from './platform.js';
import type { ResourceCheck } from './resource.js';

export type CanState = 'allowed' | 'locked' | 'hidden';

export type CanResult = {
  allowed: boolean;
  state: CanState;
  decision: Decision;
  feature?: string;
  reason?: EntitlementDenyReason;
};

export function can<R>(
  actor: Actor | null | undefined,
  permission: Permission | PlatformPermission,
  feature?: string,
  resource?: ResourceCheck<R>,
): CanResult {
  if (!actor) {
    return {
      allowed: false,
      state: 'hidden',
      decision: { allowed: false, code: 'ORG_REQUIRED', permission },
    };
  }
  const decision = authorize(actor, { permission, feature, resource });
  if (decision.allowed) return { allowed: true, state: 'allowed', decision };
  if (decision.code === 'ENTITLEMENT_MISSING') {
    return { allowed: false, state: 'locked', decision, feature: decision.feature, reason: decision.reason };
  }
  return { allowed: false, state: 'hidden', decision };
}
